import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'react-toastify';
import Loader from '../components/Loader';
import api from '../api';

const SignAgreement = () => {
  const { rentId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState(null);
  const [formData, setFormData] = useState({
    guardian_signature: '',
    guardian_name: '',
    signature_date: new Date().toISOString().split('T')[0],
    agreed_terms: false
  });
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchAgreementData = async () => {
      try {
        setLoading(true);

        // GET /api/property-application/sign-agreement/{rentId} (matches frontend route)
        const response = await api.get(`/property-application/sign-agreement/${rentId}`);
        console.log('📥 [SIGN_AGREEMENT] API Response:', response.data);
        const result = response.data;
        setData(result);

        // Initialize form data with guardian name and signature
        const guardianFullName = result.guardian?.full_name || '';
        console.log('👤 [SIGN_AGREEMENT] Guardian full name:', guardianFullName);

        setFormData(prev => {
          const newFormData = {
            ...prev,
            guardian_name: guardianFullName,
            guardian_signature: guardianFullName
          };
          console.log('📝 [SIGN_AGREEMENT] Updated form data:', newFormData);
          return newFormData;
        });

      } catch (err) {
        console.error('Error fetching agreement data:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (rentId) {
      fetchAgreementData();
    }
  }, [rentId]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!user?.permissions?.includes('rents.start')) {
      toast.error("You don't have permission to sign license agreements");
      return;
    }

    if (!formData.agreed_terms) {
      toast.error("You must agree to the terms and conditions");
      return;
    }

    try {
      setSaving(true);

      // PUT /api/property-application/sign-agreement/{rentId} (matches frontend route)
      await api.put(`/property-application/sign-agreement/${rentId}`, {
        ...formData,
        rent_id: rentId
      });

      toast.success('License agreement signed successfully!');
      navigate(`/guardians/${data.guardian.id}`); // Navigate to guardian show page

    } catch (err) {
      console.error('Error signing agreement:', err);
      toast.error('Failed to sign agreement: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Loader />;
  if (error) return <div className="alert alert-danger">Error: {error}</div>;
  if (!data) return <div className="alert alert-warning">Data not found</div>;

  return (
    <div className="container">
      {/* Breadcrumb */}
      <nav aria-label="breadcrumb">
        <ol className="breadcrumb">
          <li className="breadcrumb-item">
            <a href="/guardians">Guardians</a>
          </li>
          <li className="breadcrumb-item">
            <a href={`/guardians/${data.guardian.id}`}>{data.guardian.full_name}</a>
          </li>
          <li className="breadcrumb-item active">Sign License Agreement</li>
        </ol>
      </nav>

      <div className="row">
        <div className="col-md-12">
          <div className="panel-title title-standard mb-20">
            <h3>Sign License Agreement</h3>
          </div>

          <form onSubmit={handleSubmit}>
            {/* Guardian Information */}
            <div className="panel-title title-standard">
              <h4>Guardian Details</h4>
            </div>
            <div className="panel-group border-down margin-down">
              <div className="row">
                <div className="col-xs-12 col-sm-6">
                  <dl className="list green-list green-list-with-width">
                    <dt>Name:</dt>
                    <dd>{data.guardian.full_name} ({data.guardian.unique_guardian_number})</dd>
                    <dt>Email:</dt>
                    <dd>{data.guardian.email}</dd>
                    <dt>Phone:</dt>
                    <dd>{data.guardian.phone || data.guardian.mobile}</dd>
                    <dt>Status:</dt>
                    <dd><b>{data.guardian.status?.name}</b></dd>
                  </dl>
                </div>
                <div className="col-xs-12 col-sm-6">
                  <dl className="list green-list green-list-with-width">
                    <dt>Property:</dt>
                    <dd>{data.property.name}</dd>
                    <dt>Room:</dt>
                    <dd>{data.room.name}</dd>
                    <dt>Address:</dt>
                    <dd>{data.property.address_1}</dd>
                    <dt>Postcode:</dt>
                    <dd>{data.property.postcode}</dd>
                  </dl>
                </div>
              </div>
            </div>

            {/* Rent Information */}
            <div className="panel-title title-standard">
              <h4>Rent Details</h4>
            </div>
            <div className="panel-group border-down margin-down">
              <div className="row">
                <div className="col-xs-12 col-sm-6">
                  <dl className="list green-list green-list-with-width">
                    <dt>Weekly Rent:</dt>
                    <dd>£{data.rent.weekly_rent}</dd>
                    <dt>Deposit:</dt>
                    <dd>£{data.rent.deposit}</dd>
                    <dt>Start Date:</dt>
                    <dd>{data.rent.start_date ? new Date(data.rent.start_date).toLocaleDateString() : 'Not set'}</dd>
                    <dt>End Date:</dt>
                    <dd>{data.rent.end_date ? new Date(data.rent.end_date).toLocaleDateString() : 'Not set'}</dd>
                  </dl>
                </div>
              </div>
            </div>

            {/* License Agreement Terms */}
            <div className="panel-title title-standard">
              <h4>License Agreement Terms</h4>
            </div>
            <div className="panel-group border-down margin-down">
              <div className="well">
                <h5>Terms and Conditions</h5>
                <p>
                  By signing this license agreement, the guardian agrees to:
                </p>
                <ul>
                  <li>Occupy the property as a caretaker/guardian</li>
                  <li>Pay the weekly rent amount of £{data.rent.weekly_rent} on time</li>
                  <li>Maintain the property in good condition</li>
                  <li>Allow reasonable access for inspections</li>
                  <li>Vacate the property with appropriate notice</li>
                  <li>Comply with all property rules and regulations</li>
                </ul>
                <p>
                  <strong>Property Address:</strong> {data.property.address_1}
                  {data.property.address_2 && `, ${data.property.address_2}`}
                  {data.property.town && `, ${data.property.town}`}, {data.property.postcode}
                </p>
              </div>
            </div>

            {/* Signature Section */}
            <div className="panel-title title-standard">
              <h4>Digital Signature</h4>
            </div>
            <div className="panel-group border-down margin-down">
              <div className="row">
                <div className="col-xs-12 col-sm-4">
                  <div className="form-group">
                    <label htmlFor="guardian_name">Guardian Name *</label>
                    <input
                      type="text"
                      className="form-control"
                      id="guardian_name"
                      name="guardian_name"
                      value={formData.guardian_name}
                      onChange={handleInputChange}
                      required
                      readOnly
                      style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                    />
                  </div>
                </div>
                <div className="col-xs-12 col-sm-4">
                  <div className="form-group">
                    <label htmlFor="signature_date">Signature Date *</label>
                    <input
                      type="date"
                      className="form-control"
                      id="signature_date"
                      name="signature_date"
                      value={formData.signature_date}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>
                <div className="col-xs-12 col-sm-4">
                  <div className="form-group">
                    <label htmlFor="guardian_signature">Digital Signature *</label>
                    <input
                      type="text"
                      className="form-control"
                      id="guardian_signature"
                      name="guardian_signature"
                      value={formData.guardian_signature}
                      onChange={handleInputChange}
                      required
                      readOnly
                      style={{
                        backgroundColor: '#f5f5f5',
                        cursor: 'not-allowed',
                        fontFamily: 'cursive',
                        fontSize: '16px'
                      }}
                    />
                  </div>
                </div>
              </div>
              <div className="row">
                <div className="col-xs-12">
                  <p className="text-muted" style={{ fontSize: '12px', marginTop: '-10px', marginBottom: '15px' }}>
                    <em>Your name is automatically used as your digital signature</em>
                  </p>
                </div>
              </div>
              <div className="row">
                <div className="col-xs-12">
                  <div className="checkbox">
                    <label>
                      <input
                        type="checkbox"
                        id="agreed_terms"
                        name="agreed_terms"
                        checked={formData.agreed_terms}
                        onChange={handleInputChange}
                        required
                      />
                      {' '}
                      <strong>I agree to the terms and conditions of this license agreement *</strong>
                    </label>
                  </div>
                </div>
              </div>
            </div>

            {/* Submit Buttons */}
            <div className="form-group mt-20">
              <button
                type="submit"
                className="btn btn-success"
                disabled={saving || !formData.agreed_terms}
              >
                {saving ? 'Signing Agreement...' : 'Sign License Agreement'}
              </button>
              <button
                type="button"
                className="btn btn-secondary ml-10"
                onClick={() => navigate(-1)}
                disabled={saving}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SignAgreement;